import { queryOptions, useQuery } from '@tanstack/react-query';
import { SelfServiceSessionService } from '../../generated';
import { ResourceStatus } from '../../resources/useOcInsightsEvents';
import { AttributeChipData } from '../../../components/Prediction/types';
import { ApiError } from '../../../components/ApiLoadingGuard/ApiLoadingGuard';
import { transformEdgeEstimation } from '../edgeEstimation';

export const selfServiceEdgeEstimationKeys = {
  base: () => ['selfServiceEdgeEstimation'] as const,
  one: ({ jobsiteId, sessionId, imageIndex, imageStatus, edgeSizeStatus }: SelfServiceEdgeEstimationOptionsProps) => [
    ...selfServiceEdgeEstimationKeys.base(),
    jobsiteId,
    sessionId,
    imageIndex,
    imageStatus,
    edgeSizeStatus,
  ],
};

interface SelfServiceEdgeEstimationOptionsProps {
  jobsiteId: string;
  sessionId: string;
  imageIndex: number | null;
  imageStatus: ResourceStatus;
  edgeSizeStatus: ResourceStatus;
  displayEdgeEstimation?: boolean;
}

function selfServiceEdgeEstimationOptions({
  jobsiteId,
  sessionId,
  imageIndex,
  imageStatus,
  edgeSizeStatus,
  displayEdgeEstimation = false,
}: SelfServiceEdgeEstimationOptionsProps) {
  return queryOptions<AttributeChipData[], ApiError>({
    queryKey: selfServiceEdgeEstimationKeys.one({ jobsiteId, sessionId, imageIndex, imageStatus, edgeSizeStatus }),
    queryFn: async () => {
      if (!jobsiteId || !sessionId || imageIndex === null)
        throw new Error('jobsiteId, sessionId and imageIndex are required');

      const response = await SelfServiceSessionService.selfServiceSessionControllerGetSessionImageEdgeSizes({
        path: { jobsiteId, sessionId, index: imageIndex },
      });

      if (response.data !== undefined) {
        return transformEdgeEstimation(response.data.output);
      } else {
        throw (response as any).error || new Error(response.response?.statusText || 'Unknown error');
      }
    },
    enabled:
      !!jobsiteId &&
      !!sessionId &&
      imageStatus === 'finished' &&
      edgeSizeStatus === 'finished' &&
      displayEdgeEstimation,
  });
}

export function useSelfServiceEdgeEstimation(props: SelfServiceEdgeEstimationOptionsProps) {
  return useQuery(selfServiceEdgeEstimationOptions(props));
}
